import React from 'react'
import styled from '@emotion/styled'
import { roundDownDecimal } from '@mobi/betslip/helpers/calculator/misc'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { AnimatedNumberTransition } from '@mobi/component-library/Misc/AnimatedNumberTransition'
import { Currency } from '@mobi/utils/money'

const enum LocalConstants {
  PriceChangeUpClass = 'js-price--up',
  PriceChangeDownClass = 'js-price--down',
  PriceChangeActiveClass = 'js-price--changed',
  PriceShiftClass = 'js-price--shifted',
  SecondaryPriceClass = 'js-price--secondary',
  BoostedPriceClass = 'js-price--boosted',
}

interface PriceProps {
  value: number
  valueLastSeen?: number | null
  isIndicatorShifted?: boolean
  isSecondaryDisplay?: boolean
  roundDown?: boolean
  priceBoost?: number
  shouldDisplayAsBoosted?: boolean
  isScratchedOut?: boolean
}

export const Price: React.FC<PriceProps> = ({
  value,
  isScratchedOut = false,
  valueLastSeen,
  isIndicatorShifted = false,
  isSecondaryDisplay = false,
  roundDown = false,
  priceBoost = 0,
  shouldDisplayAsBoosted = false,
}) => {
  const priceChangeDirection: PriceChangeDirection =
    value && valueLastSeen && value !== valueLastSeen
      ? valueLastSeen < value
        ? 'UP'
        : 'DOWN'
      : null

  const classNames = []
  if (priceChangeDirection) classNames.push(getPriceChangeClass(priceChangeDirection))
  if (isIndicatorShifted) classNames.push(LocalConstants.PriceShiftClass)
  if (isSecondaryDisplay) classNames.push(LocalConstants.SecondaryPriceClass)
  if (priceBoost > 0 || shouldDisplayAsBoosted) classNames.push(LocalConstants.BoostedPriceClass)

  const displayValue = new Currency(roundDown ? roundDownDecimal(value) : value).format()

  return (
    <PriceStyled className={classNames.join(' ')} data-testid='Price'>
      <span className={isScratchedOut ? 'price-scratched-out' : undefined}>
        {priceBoost ? (
          <AnimatedNumberTransition
            start={displayValue}
            end={new Currency(value).add(priceBoost).format()}
            separatorCharacters='$'
          />
        ) : (
          displayValue
        )}
      </span>

      <span>
        <span>
          {priceChangeDirection === 'UP' && (
            <Icon name='SolidTriangle' size='0.8rem' color={colors.success[500]} />
          )}
        </span>
        <span>
          {priceChangeDirection === 'DOWN' && (
            <Icon name='SolidTriangle' size='0.8rem' color={colors.error[600]} />
          )}
        </span>
      </span>
    </PriceStyled>
  )
}

// =============
// Local Helpers
// =============

function getPriceChangeClass(direction: PriceChangeDirection): string {
  if (direction === 'UP') return LocalConstants.PriceChangeUpClass
  if (direction === 'DOWN') return LocalConstants.PriceChangeDownClass
  return ''
}

// ======
// Styles
// ======

const PriceStyled = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  fontSize: font.size.lg.fontSize,
  fontWeight: font.weight.medium,
  letterSpacing: font.size.lg.letterSpacing,
  lineHeight: 1,
  color: colors.black,
  backgroundColor: 'transparent',
  transition: 'background-color ease 300ms',

  // Price Value
  '> span:nth-of-type(1)': {
    transition: 'all ease 400ms',

    '&.price-scratched-out': {
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontWeight: font.weight.regular,
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      textDecorationLine: 'line-through',
      color: colors.neutral[600],
    },
  },

  // Change Indicators
  '> span:nth-of-type(2)': {
    position: 'absolute',
    right: 0,
    transition: 'opacity ease 400ms',
    opacity: 0,
    transform: 'none',

    '> span': { display: 'flex', alignItems: 'center' },
    '> span:last-of-type': { transform: 'rotate(180deg)' },
  },

  [`&.${LocalConstants.PriceChangeUpClass}`]: {
    '> span:nth-of-type(1)': {
      color: colors.success[500],
      transform: 'translateX(-1.2rem)',
    },
    '> span:nth-of-type(2)': { opacity: 1 },
  },

  [`&.${LocalConstants.PriceChangeDownClass}`]: {
    '> span:nth-of-type(1)': {
      color: colors.error[600],
      transform: 'translateX(-1.2rem)',
    },
    '> span:nth-of-type(2)': { opacity: 1 },
  },

  [`&.${LocalConstants.PriceShiftClass}`]: {
    '> span:nth-of-type(1)': { transform: 'none' },
    '> span:nth-of-type(2)': { transform: 'translateX(1.2rem)' },
  },

  [`&.${LocalConstants.SecondaryPriceClass}`]: {
    [`&.${LocalConstants.PriceChangeUpClass} > span:nth-of-type(1)`]: { paddingLeft: '1.2rem' },
    [`&.${LocalConstants.PriceChangeDownClass} > span:nth-of-type(1)`]: { paddingLeft: '1.2rem' },
  },

  [`&.${LocalConstants.BoostedPriceClass}`]: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.smx2,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    fontWeight: font.weight.bold,
    color: colors.orange[800],
    borderRadius: radius.md,
    border: '1px solid rgba(255, 255, 255, 0.50)',
    paddingInline: spacing.smx1,
    background:
      'linear-gradient(321deg, rgba(255, 255, 255, 0.00) 46.15%, rgba(255, 255, 255, 0.60) 87.05%, rgba(255, 255, 255, 0.00) 127.95%), linear-gradient(180deg, #F9D636 0%, #F9B036 100%)',

    '> span:nth-of-type(1)': {
      display: 'flex',
      alignItems: 'baseline',
      gap: '0.4rem',
    },
  },
})

// =====
// Types
// =====

type PriceChangeDirection = 'UP' | 'DOWN' | null
